import { Title, Meta } from "@solidjs/meta";
import { Component, JSX } from "solid-js";
import { env } from "~/config/env";

/**
 * SEO Component Props
 */
export interface SEOProps {
  /** Page title (will be suffixed with " - ad.audio") */
  title: string;
  /** Meta description for the page */
  description: string;
  /** Canonical URL for the page (e.g., "https://www.ad.audio/") */
  url?: string;
  /** Open Graph image URL */
  image?: string;
  /** Image alt text for accessibility and LLM understanding */
  imageAlt?: string;
  /** Image width (recommended for OG) */
  imageWidth?: number;
  /** Image height (recommended for OG) */
  imageHeight?: number;
  /** Twitter card type (default: "summary_large_image") */
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  /** Robots meta directive (default: "index, follow") */
  robots?: string;
  /** Locale (default: "de_DE") */
  locale?: string;
  /** Site name (default: "ad.audio") */
  siteName?: string;
  /** Keywords for LLM context (comma-separated) */
  keywords?: string;
  /** Append " - ad.audio" to the page title (default: true) */
  appendSiteName?: boolean;
  /** Structured data (JSON-LD) for rich snippets and LLMs */
  structuredData?: Record<string, any>;
  /** Additional meta tags */
  children?: JSX.Element;
}

/**
 * SEO Component
 *
 * Reusable component for managing page metadata.
 * Follows SolidStart best practices for server-side rendering.
 *
 * @example
 * ```tsx
 * <SEO
 *   title="Über uns"
 *   description="Erfahre mehr über ad.audio"
 *   url="https://www.ad.audio/ueber-uns"
 *   image="https://www.ad.audio/og-image.jpg"
 *   imageAlt="ad.audio Logo"
 * />
 * ```
 */
/**
 * Site-wide fallback description. Empty meta/og/twitter description tags are
 * treated as missing by social crawlers, so always emit at least this default.
 */
const DEFAULT_DESCRIPTION =
  "Holistische Audio Kampagnen auf digitalen Premium-Inventaren. Nationales Radio, Online Audio Streams & redaktionell hochwertige Podcasts.";

// Dimensions of the default /og-image.png (Open Graph standard 1.91:1)
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;

export const SEO: Component<SEOProps> = (props) => {
  const fullTitle = () =>
    props.appendSiteName === false ? props.title : `${props.title} - ad.audio`;
  const description = () => props.description || DEFAULT_DESCRIPTION;
  const image = () => props.image || `${env.siteUrl}/og-image.png`;
  const imageWidth = () => (props.image ? props.imageWidth : DEFAULT_IMAGE_WIDTH);
  const imageHeight = () => (props.image ? props.imageHeight : DEFAULT_IMAGE_HEIGHT);
  const twitterCard = () => props.twitterCard || "summary_large_image";
  const robots = () => props.robots || "index, follow";
  const locale = () => props.locale || "de_DE";
  const siteName = () => props.siteName || "ad.audio";

  const defaultStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    headline: props.title,
    description: description(),
    url: props.url,
    ...(image() && {
      image: {
        "@type": "ImageObject",
        url: image(),
        ...(props.imageAlt && { caption: props.imageAlt }),
        ...(imageWidth() && { width: imageWidth() }),
        ...(imageHeight() && { height: imageHeight() }),
      },
    }),
  });

  return (
    <>
      <Title>{fullTitle()}</Title>

      {props.url && <link rel="canonical" href={props.url} />}

      <Meta name="description" content={description()} />
      <Meta name="robots" content={robots()} />
      {props.keywords && <Meta name="keywords" content={props.keywords} />}

      <Meta property="og:site_name" content={siteName()} />
      <Meta property="og:title" content={fullTitle()} />
      <Meta property="og:description" content={description()} />
      <Meta property="og:type" content="website" />
      <Meta property="og:locale" content={locale()} />
      {props.url && <Meta property="og:url" content={props.url} />}

      <Meta property="og:image" content={image()} />
      {props.imageAlt && <Meta property="og:image:alt" content={props.imageAlt} />}
      {imageWidth() && <Meta property="og:image:width" content={imageWidth()!.toString()} />}
      {imageHeight() && <Meta property="og:image:height" content={imageHeight()!.toString()} />}

      <Meta name="twitter:card" content={twitterCard()} />
      <Meta name="twitter:title" content={fullTitle()} />
      <Meta name="twitter:description" content={description()} />
      <Meta name="twitter:image" content={image()} />
      {props.imageAlt && <Meta name="twitter:image:alt" content={props.imageAlt} />}

      <script type="application/ld+json">
        {JSON.stringify(props.structuredData || defaultStructuredData())}
      </script>

      {props.children}
    </>
  );
};
